import React from "react";
import { Crown, Wrench, Trophy, User, Check } from "lucide-react";

/**
 * Persona split: four cards, one per buyer/user.
 * All cards weighted equally — every seat matters.
 */
const personas = [
  {
    Icon: Crown,
    slug: "founder",
    label: "founder",
    headline: "You're 8 weeks from board day.",
    body: "Know what the number will actually be — before it becomes a crisis. Walk into the board meeting with the story, not the surprise.",
    bullets: [
      "Weekly Monday briefing lands at 06:00 — before your first meeting",
      "See the €-delta since last week, and which deals moved it",
      "One page. No dashboard-diving. No RevOps hire required.",
    ],
  },
  {
    Icon: Wrench,
    slug: "revops",
    label: "revenue_ops",
    headline: "Stop rebuilding the same Sheet every Sunday.",
    body: "Recrysta ships the pipeline health view your team asks for every week. You get your Sunday nights back — and get to work on strategy instead.",
    bullets: [
      "Append-only deal history — full audit trail, no data drift",
      "Stage-conversion math done for you, not another VLOOKUP",
      "Per-rep coverage, cycle time, forecast reliability out of the box",
    ],
  },
  {
    Icon: Trophy,
    slug: "sales_leader",
    label: "sales_leader",
    headline: "Spot the slip before the QBR.",
    body: "Know which reps are ahead and which are quietly falling behind — while there's still time in the quarter to coach them out of it.",
    bullets: [
      "Per-rep win rates on your real sample, not vanity averages",
      "Deals at risk flagged with reasons, not just red dots",
      "Team leaderboard ranked by pipeline coverage, not activity theatre",
    ],
  },
  {
    Icon: User,
    slug: "sales_rep",
    label: "sales_rep",
    headline: "Know your own number — before your manager asks.",
    body: "See your open pipeline, your at-risk deals, and your win rate on your own book. Coach yourself instead of getting coached.",
    bullets: [
      "Your pipeline vs your quota, updated daily — no forecast calls needed",
      "Your deals silently slipping flagged early with a specific reason",
      "Your win rate, cycle time, and forecast reliability — so you know what to work on",
    ],
  },
];

export const PersonaSplit = () => {
  return (
    <section
      className="mx-auto px-6 lg:px-10 py-20"
      style={{ maxWidth: "1100px" }}
      data-testid="landing-persona-split"
    >
      <div className="text-center max-w-2xl mx-auto mb-14">
        <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-slate-500">
          <span className="text-emerald-500/70">/</span>built_for
        </div>
        <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
          One product. Four seats at the same table.
        </h2>
        <p className="mt-3 text-slate-600 leading-relaxed">
          Every plan works for the whole revenue team — same data, four angles on Monday morning.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {personas.map((p) => (
          <PersonaCard key={p.slug} {...p} />
        ))}
      </div>
    </section>
  );
};

const PersonaCard = ({ Icon, slug, label, headline, body, bullets }) => (
  <article
    className="relative rounded-2xl p-6 flex flex-col bg-white border border-slate-200 text-slate-900 hover:border-emerald-500/40 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
    data-testid={`landing-persona-${slug}`}
  >
    <div className="h-10 w-10 rounded-lg flex items-center justify-center bg-slate-900 text-white">
      <Icon className="h-5 w-5" />
    </div>
    <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.24em] text-slate-500">
      <span className="text-emerald-500/70">/</span>
      {label}
    </div>
    <h3 className="mt-2 text-lg font-extrabold tracking-tight text-slate-900 leading-tight">
      {headline}
    </h3>
    <p className="mt-3 text-sm leading-relaxed text-slate-600">
      {body}
    </p>
    <ul className="mt-5 space-y-2.5 text-sm">
      {bullets.map((b) => (
        <li key={b} className="flex items-start gap-2">
          <Check className="h-4 w-4 mt-0.5 shrink-0 text-emerald-600" />
          <span className="text-slate-700">{b}</span>
        </li>
      ))}
    </ul>
  </article>
);

export default PersonaSplit;
